import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Stethoscope, FileText, Zap } from 'lucide-react';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import type { TenantHomePageContent } from '../../types/site-pages';
import { buildTenantPath } from '../../utils/tenantSlug';

interface HeroProps {
  mode?: 'global' | 'tenant';
  tenantContent?: TenantHomePageContent | null;
}

const Hero = ({ mode = 'global', tenantContent }: HeroProps) => {
  const { effectiveTenantSlug } = useClinicProfile();

  if (mode === 'tenant') {
    const hero = tenantContent?.hero;
    const heroTitle = hero?.title || 'Cuidado especializado para sua reabilitação';
    const heroSubtitle =
      hero?.subtitle ||
      'Nossa equipe oferece serviços especializados em fisioterapia e reabilitação, com foco na qualidade de vida e recuperação dos pacientes.';
    const desktopImageUrl = hero?.desktopImage?.downloadUrl || hero?.mobileImage?.downloadUrl || '';
    const mobileImageUrl = hero?.mobileImage?.downloadUrl || hero?.desktopImage?.downloadUrl || '';
    const imageAlt =
      hero?.desktopImage?.alt ||
      hero?.mobileImage?.alt ||
      'Imagem institucional da clínica';
    const primaryCtaLabel = hero?.primaryCta?.label || 'Agendar Consulta';
    const primaryCtaPath = hero?.primaryCta?.path || '/agendar';
    const secondaryCtaLabel = hero?.secondaryCta?.label || 'Nossos Serviços';
    const secondaryCtaPath = hero?.secondaryCta?.path || '/servicos';

    return (
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0 z-0 overflow-hidden">
          {(desktopImageUrl || mobileImageUrl) && (
            <picture>
              {mobileImageUrl ? <source media="(max-width: 767px)" srcSet={mobileImageUrl} /> : null}
              <img
                src={desktopImageUrl || mobileImageUrl}
                alt={imageAlt}
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            </picture>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 to-primary-900/50" />
        </div>

        <div className="container mx-auto px-4 z-10">
          <div className="max-w-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-6"
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-neutral-100 mb-8"
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to={buildTenantPath(primaryCtaPath, effectiveTenantSlug)}
                className="bg-primary-600 hover:bg-primary-700 text-white py-3 px-6 rounded-md font-medium transition-colors text-center"
              >
                {primaryCtaLabel}
              </Link>
              <Link
                to={buildTenantPath(secondaryCtaPath, effectiveTenantSlug)}
                className="bg-white hover:bg-neutral-100 text-primary-700 py-3 px-6 rounded-md font-medium transition-colors text-center"
              >
                {secondaryCtaLabel}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  /* ─── GLOBAL MODE: Landing page de venda de licenças ─── */
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-slate-950">
      {/* Background decorativo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute -top-20 -left-20 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="absolute top-1/3 -right-24 h-[600px] w-[600px] rounded-full bg-indigo-500/10 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-violet-500/10 blur-[100px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.04),transparent_50%)]" />
        {/* Grid sutil */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="container mx-auto px-4 z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* ─── Coluna de texto ─── */}
          <div className="max-w-2xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-flex items-center gap-2"
            >
              <img
                src="/logos/revahub-mark.svg"
                alt="Revahub"
                className="h-10 w-10 object-contain"
              />
              <span className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-sm font-medium text-cyan-200 tracking-wide">
                Plataforma de Gestão para Clínicas
              </span>
            </motion.div>

            {/* Headline — foco na dor e na solução */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl font-display font-bold text-white leading-[1.1] mb-6"
            >
              Avaliações clínicas com{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                laudo em segundos.
              </span>
            </motion.h1>

            {/* Subheadline — benefício claro */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed"
            >
              O RevaHub gera laudos profissionais com{' '}
              <strong className="text-white">interpretação clínica automatizada</strong>{' '}
              para 13 tipos de avaliação — respiratórias, cardiovasculares e oncológicas.
              Além disso, centraliza agenda, prontuário e faturamento em uma única plataforma.
            </motion.p>

            {/* Mini feature pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="flex flex-wrap gap-2 mb-8"
            >
              {[
                { icon: Stethoscope, label: '13 avaliações clínicas' },
                { icon: FileText, label: 'Laudos PDF automáticos' },
                { icon: Zap, label: 'Interpretação em segundos' },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs font-medium text-slate-300"
                >
                  <Icon size={13} className="text-cyan-400" />
                  {label}
                </span>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Link
                to="/cadastro"
                className="group inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 py-3.5 px-7 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/30"
              >
                Agende uma Demonstração
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <a
                href="#avaliacoes"
                className="group inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white py-3.5 px-7 rounded-lg font-medium transition-all duration-200"
              >
                <Play size={16} className="text-cyan-400" />
                Ver avaliações disponíveis
              </a>
            </motion.div>

            {/* Micro social proof */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="flex items-center gap-4 text-sm text-slate-400"
            >
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-8 w-8 rounded-full border-2 border-slate-950 bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-[10px] font-bold text-white"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <span>
                <strong className="text-white">+50 clínicas</strong> já
                otimizaram sua gestão com o RevaHub
              </span>
            </motion.div>
          </div>

          {/* ─── Coluna visual: Mockup do laudo de avaliação ─── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            {/* Glow atrás do card */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 via-transparent to-violet-500/20 blur-2xl" />

            <div className="relative rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur-sm p-1 shadow-2xl">
              {/* Barra de título do "browser" */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                <div className="flex gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-red-400/80" />
                  <span className="h-3 w-3 rounded-full bg-yellow-400/80" />
                  <span className="h-3 w-3 rounded-full bg-green-400/80" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="h-6 rounded-md bg-white/5 flex items-center px-3 text-xs text-slate-500">
                    app.revahub.com.br/admin/avaliacoes
                  </div>
                </div>
              </div>

              {/* Conteúdo simulado — lista de avaliações */}
              <div className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-white">Avaliações Recentes</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">13 tipos disponíveis</div>
                  </div>
                  <div className="h-8 w-28 rounded-md bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[10px] text-cyan-400 font-medium">
                    + Nova Avaliação
                  </div>
                </div>

                {/* Cards de avaliações recentes */}
                {[
                  { type: 'TCPE', patient: 'Maria S.', status: 'Laudo gerado', statusColor: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30', time: '28s' },
                  { type: 'TC6', patient: 'João P.', status: 'Laudo gerado', statusColor: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30', time: '12s' },
                  { type: 'CAT', patient: 'Ana L.', status: 'Laudo gerado', statusColor: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30', time: '8s' },
                  { type: 'SGRQ', patient: 'Carlos R.', status: 'Em andamento', statusColor: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30', time: '—' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.8 + idx * 0.1 }}
                    className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-3"
                  >
                    <div className="h-8 w-12 rounded bg-white/5 flex items-center justify-center text-[10px] font-bold text-cyan-400">
                      {item.type}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-medium text-white">{item.patient}</div>
                      <div className="text-[10px] text-slate-500">Hoje, 14:30</div>
                    </div>
                    <div className={`rounded-full border px-2 py-0.5 text-[9px] font-medium ${item.statusColor}`}>
                      {item.status}
                    </div>
                    {item.time !== '—' && (
                      <div className="text-[10px] text-slate-500">
                        <Zap size={10} className="inline text-cyan-400" /> {item.time}
                      </div>
                    )}
                  </motion.div>
                ))}

                {/* Mini gráfico de evolução */}
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] text-slate-500 mb-2">Evolução TC6 — Maria S.</div>
                  <div className="flex items-end gap-1.5 h-16">
                    {[320, 340, 355, 380, 410, 425, 440, 460].map((val, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${(val / 500) * 100}%` }}
                        transition={{ duration: 0.5, delay: 1.2 + i * 0.06 }}
                        className="flex-1 rounded-sm bg-gradient-to-t from-cyan-500/60 to-cyan-400/20 relative group"
                      >
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[7px] text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          {val}m
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[8px] text-slate-600">Jan</span>
                    <span className="text-[8px] text-slate-600">Ago</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
